import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { RefreshCw, Sparkles, AlertTriangle, Table2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Entity Preview Sheet — shows a sample of rows for one semantic entity,
 * with every base attribute + every calculated dimension applied. Great
 * for validating bucket boundaries and formula outputs against real data.
 *
 * Props:
 *   open, onOpenChange
 *   entityId, entityName
 */
export function EntityPreviewSheet({ open, onOpenChange, entityId, entityName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(100);

  const load = React.useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await api.previewEntityRows(entityId, { limit });
      setData(res);
    } catch (e) {
      setData({ error: e.message || 'Failed to load preview', columns: [], rows: [] });
    } finally {
      setLoading(false);
    }
  }, [entityId, limit]);

  useEffect(() => {
    if (open && entityId) load();
  }, [open, entityId, load]);

  const cols = data?.columns || [];
  const rows = data?.rows || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto" data-testid="entity-preview-sheet">
        <SheetHeader className="mb-3">
          <SheetTitle className="flex items-center gap-2">
            <Table2 className="w-4 h-4 text-emerald-500" />
            Preview: {entityName || entityId}
          </SheetTitle>
          <SheetDescription>
            Sample of raw records with every base attribute and calculated dimension applied.
            Aggregated calculated measures are not shown at row level.
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-muted-foreground flex items-center gap-1.5">Rows
            <select
              value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              className="px-2 py-1 text-xs rounded border border-border/50 bg-muted/40"
              data-testid="preview-limit"
            >
              {[25, 50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-7 text-[11px] gap-1" data-testid="preview-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          {data && (
            <span className="text-[11px] text-muted-foreground ml-auto">
              {rows.length} row{rows.length === 1 ? '' : 's'} · {cols.length} column{cols.length === 1 ? '' : 's'}
              {data.skipped_calc_measures > 0 && (
                <span className="ml-2 text-amber-500">
                  · {data.skipped_calc_measures} aggregated measure{data.skipped_calc_measures === 1 ? '' : 's'} hidden
                </span>
              )}
            </span>
          )}
        </div>

        {data?.error && (
          <div className="mb-3 p-3 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-400 text-sm flex items-start gap-2" data-testid="preview-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Preview failed</div>
              <div className="text-xs mt-0.5 opacity-80">{data.error}</div>
            </div>
          </div>
        )}

        {!loading && !data?.error && cols.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-border/50" data-testid="preview-table-wrapper">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  {cols.map(c => (
                    <th key={c.name} className="text-left px-3 py-2 font-medium text-foreground/90 whitespace-nowrap border-b border-border/50">
                      <div className="flex items-center gap-1.5">
                        <span>{c.display_name}</span>
                        {c.is_calculated && (
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${
                              c.subtype === 'bucket'
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : 'bg-sky-500/15 text-sky-400 border border-sky-500/30'
                            }`}
                            title={c.formula}
                          >
                            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
                            {c.subtype === 'bucket' ? 'BUCKET' : 'CALC'}
                          </span>
                        )}
                      </div>
                      <div className="text-[9px] text-muted-foreground/80 font-normal">{c.data_type}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/20 hover:bg-muted/20" data-testid={`preview-row-${i}`}>
                    {cols.map(c => {
                      const v = r[c.name];
                      const display = v === null || v === undefined ? <span className="text-muted-foreground/70 italic">null</span>
                                    : typeof v === 'object' ? JSON.stringify(v)
                                    : String(v);
                      return (
                        <td key={c.name} className={`px-3 py-1.5 whitespace-nowrap ${c.is_calculated ? 'bg-emerald-500/[0.04]' : ''}`}>{display}</td>
                      );
                    })}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-muted-foreground text-xs">No rows returned.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data?.skipped_calc_measure_names?.length > 0 && (
          <div className="mt-3 text-[11px] text-muted-foreground">
            Hidden aggregated measures: {data.skipped_calc_measure_names.map(n => <code key={n} className="mx-1 text-primary/70">{n}</code>)}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
